import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Sparkles, 
  BookOpen, 
  Wand2, 
  Edit, 
  Check, 
  Send,
  Clock,
  Calendar,
  Loader2,
  RefreshCw,
  Eye,
  Plus,
  Share2,
  TrendingUp,
  Facebook,
  Instagram,
  Linkedin,
  Mail,
  MessageSquare,
  Copy,
  Download,
  BarChart3,
  Users
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminSoulFoodTab() {
  const [soulFoods, setSoulFoods] = useState([]);
  const [reflections, setReflections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [shareAnalytics, setShareAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [newSoulFood, setNewSoulFood] = useState({
    title: '',
    quote_or_scripture: '',
    source: '',
    is_scripture: true,
    reflection: '',
    theme: '',
    status: 'draft'
  });

  useEffect(() => {
    fetchContent();
  }, []);

  // ALL endpoints use /api/debug/sf-* pattern (flat paths work on production)
  const SF_BASE = `${API_URL}/api/debug`;

  const fetchContent = async () => {
    try {
      // Uses soul-food-check which is proven to work on production
      const res = await axios.get(`${API_URL}/api/debug/soul-food-check`);
      setSoulFoods(res.data.soul_foods || []);
      setReflections(res.data.reflections || []);
    } catch (error) {
      console.error('Error fetching content:', error);
      toast.error(`Failed to load: ${error?.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchShareAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${SF_BASE}/sf-analytics`);
      setShareAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching share analytics:', error);
      toast.error('Failed to load share analytics');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      // Kick off the background job (returns immediately with a job_id)
      const startRes = await axios.post(`${SF_BASE}/sf-generate`);
      const jobId = startRes?.data?.job_id;
      toast.info('Generation started — this takes about 15–30 seconds...');

      // Poll for status (max ~90s of polling)
      const pollUrl = `${SF_BASE}/sf-generate-status${jobId ? `?job_id=${jobId}` : ''}`;
      const start = Date.now();
      const TIMEOUT_MS = 90000;
      const POLL_MS = 3000;

      const finalStatus = await new Promise((resolve, reject) => {
        const tick = async () => {
          try {
            const r = await axios.get(pollUrl);
            const status = r?.data?.status;
            if (status === 'completed' || status === 'failed') {
              return resolve(r.data);
            }
            if (Date.now() - start > TIMEOUT_MS) {
              return reject(new Error('Generation is still running. Refresh in a minute to see the result.'));
            }
            setTimeout(tick, POLL_MS);
          } catch (err) {
            reject(err);
          }
        };
        tick();
      });

      if (finalStatus?.status === 'completed') {
        toast.success('Content generated! Check drafts to review.');
        fetchContent();
      } else {
        toast.error(`Generation failed: ${finalStatus?.error || 'Unknown error'}`);
      }
    } catch (error) {
      const detail = error?.response?.data?.detail || error.message;
      toast.error(`Failed to generate: ${detail}`);
    } finally {
      setGenerating(false);
    }
  };

  const handlePublish = async (type, id) => {
    try {
      const token = localStorage.getItem('token');
      const kind = type === 'soul_food' ? 'sf' : 'ref';
      await axios.post(`${SF_BASE}/sf-publish?kind=${kind}&id=${id}`);
      toast.success('Published successfully!');
      fetchContent();
    } catch (error) {
      toast.error('Failed to publish');
    }
  };

  const handleUpdate = async (type, id, updates) => {
    try {
      const token = localStorage.getItem('token');
      const kind = type === 'soul_food' ? 'sf' : 'ref';
      await axios.post(`${SF_BASE}/sf-update?kind=${kind}&id=${id}`, updates);
      toast.success('Updated successfully!');
      setEditMode(false);
      setSelectedItem(null);
      fetchContent();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleCreateSoulFood = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${SF_BASE}/sf-create`, newSoulFood);
      
      toast.success('Soul Food created!');
      setShowCreateDialog(false);
      setNewSoulFood({
        title: '',
        quote_or_scripture: '',
        source: '',
        is_scripture: true,
        reflection: '',
        theme: '',
        status: 'draft'
      });
      fetchContent();
    } catch (error) {
      toast.error('Failed to create Soul Food');
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      draft: 'bg-yellow-100 text-yellow-700',
      published: 'bg-green-100 text-green-700',
      archived: 'bg-gray-100 text-gray-700'
    };
    return <Badge className={colors[status] || colors.draft}>{status}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Actions */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-heading text-forest">Soul Food & Reflections</h2>
          <p className="text-gray-500">AI-generated weekly inspiration for Inner Circle members</p>
          <p className="text-xs text-gray-300">UI v16</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={fetchContent}
            data-testid="refresh-content-btn"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowCreateDialog(true)}
            data-testid="create-soul-food-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Manual
          </Button>
          <Button
            onClick={handleGenerate}
            disabled={generating}
            className="bg-gradient-to-r from-purple-500 to-pink-500"
            data-testid="generate-content-btn"
          >
            {generating ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Wand2 className="h-4 w-4 mr-2" />
            )}
            Generate with AI
          </Button>
        </div>
      </div>

      {/* Schedule Info */}
      <Card className="border-purple-100 bg-purple-50">
        <CardContent className="py-4">
          <div className="flex items-center gap-4">
            <Calendar className="h-5 w-5 text-purple-600" />
            <div>
              <p className="font-medium text-purple-900">Auto-Schedule</p>
              <p className="text-sm text-purple-600">
                Content generates Saturday 8 PM UTC and auto-publishes Sunday 7 AM UTC
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="soul_food" className="space-y-4">
        <TabsList>
          <TabsTrigger value="soul_food" className="gap-2">
            <Sparkles className="h-4 w-4" />
            Soul Food ({soulFoods.length})
          </TabsTrigger>
          <TabsTrigger value="reflections" className="gap-2">
            <BookOpen className="h-4 w-4" />
            Reflections ({reflections.length})
          </TabsTrigger>
          <TabsTrigger value="analytics" className="gap-2" onClick={() => !shareAnalytics && fetchShareAnalytics()}>
            <BarChart3 className="h-4 w-4" />
            Share Analytics
          </TabsTrigger>
        </TabsList>

        {/* Soul Food Tab */}
        <TabsContent value="soul_food" className="space-y-4">
          {soulFoods.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-gray-500">
                <Sparkles className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No Soul Food created yet.</p>
                <p className="text-sm">Click "Generate with AI" to create weekly content.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {soulFoods.map((sf) => (
                <Card key={sf.id} className="border hover:border-purple-200 transition-colors">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CardTitle className="text-lg">{sf.title || 'Untitled'}</CardTitle>
                        {getStatusBadge(sf.status)}
                        {sf.ai_generated && (
                          <Badge variant="outline" className="text-purple-600 border-purple-300">
                            <Wand2 className="h-3 w-3 mr-1" />
                            AI
                          </Badge>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedItem({ type: 'soul_food', data: sf });
                            setEditData(sf);
                            setEditMode(true);
                          }}
                          data-testid={`edit-soul-food-${sf.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        {sf.status === 'draft' && (
                          <Button
                            size="sm"
                            onClick={() => handlePublish('soul_food', sf.id)}
                            className="bg-green-500 hover:bg-green-600"
                            data-testid={`publish-soul-food-${sf.id}`}
                          >
                            <Send className="h-4 w-4 mr-1" />
                            Publish
                          </Button>
                        )}
                      </div>
                    </div>
                    <CardDescription>
                      Week of {sf.week_of} • {sf.theme && `Theme: ${sf.theme}`}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="italic text-gray-700">"{sf.quote_or_scripture}"</p>
                      <p className="text-sm text-gray-500 mt-1">— {sf.source}</p>
                    </div>
                    <p className="mt-3 text-gray-600 text-sm">{sf.reflection}</p>
                    <div className="flex items-center gap-4 mt-3 text-xs text-gray-400">
                      <span>❤️ {sf.likes?.length || 0} likes</span>
                      <span>💬 {sf.comments?.length || 0} comments</span>
                      <span className="flex items-center gap-1">
                        <Share2 className="h-3 w-3" />
                        {sf.share_count || 0} shares
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Reflections Tab */}
        <TabsContent value="reflections" className="space-y-4">
          {reflections.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-gray-500">
                <BookOpen className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No weekly reflections created yet.</p>
                <p className="text-sm">Click "Generate with AI" to create reflection prompts.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {reflections.map((ref) => (
                <Card key={ref.id} className="border hover:border-amber-200 transition-colors">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CardTitle className="text-lg">Week of {ref.week_of}</CardTitle>
                        {getStatusBadge(ref.status)}
                        {ref.ai_generated && (
                          <Badge variant="outline" className="text-amber-600 border-amber-300">
                            <Wand2 className="h-3 w-3 mr-1" />
                            AI
                          </Badge>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedItem({ type: 'reflections', data: ref });
                            setEditData(ref);
                            setEditMode(true);
                          }}
                          data-testid={`edit-reflections-${ref.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        {ref.status === 'draft' && (
                          <Button
                            size="sm"
                            onClick={() => handlePublish('reflections', ref.id)}
                            className="bg-green-500 hover:bg-green-600"
                            data-testid={`publish-reflections-${ref.id}`}
                          >
                            <Send className="h-4 w-4 mr-1" />
                            Publish
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {ref.prompts?.map((prompt, idx) => (
                        <div key={prompt.id} className="flex items-start gap-2 p-2 bg-amber-50 rounded-lg">
                          <Badge variant="outline" className="shrink-0 text-amber-600">
                            {prompt.theme}
                          </Badge>
                          <div>
                            <p className="text-sm text-gray-800">{prompt.prompt}</p>
                            {prompt.follow_up && (
                              <p className="text-xs text-gray-500 mt-1">{prompt.follow_up}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-4">
          {analyticsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
            </div>
          ) : !shareAnalytics ? (
            <Card>
              <CardContent className="py-12 text-center">
                <BarChart3 className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-500">Click to load share analytics</p>
                <Button onClick={fetchShareAnalytics} className="mt-4">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Load Analytics
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="border-purple-100 bg-gradient-to-br from-purple-50 to-pink-50">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-purple-600 font-medium">Total Shares</p>
                        <p className="text-3xl font-bold text-purple-900">{shareAnalytics.total_shares}</p>
                      </div>
                      <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center">
                        <Share2 className="h-6 w-6 text-purple-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-blue-100 bg-gradient-to-br from-blue-50 to-cyan-50">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-blue-600 font-medium">Unique Sharers</p>
                        <p className="text-3xl font-bold text-blue-900">{shareAnalytics.unique_sharers}</p>
                      </div>
                      <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                        <Users className="h-6 w-6 text-blue-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-green-100 bg-gradient-to-br from-green-50 to-emerald-50">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-green-600 font-medium">Avg Shares/User</p>
                        <p className="text-3xl font-bold text-green-900">
                          {shareAnalytics.unique_sharers > 0 
                            ? (shareAnalytics.total_shares / shareAnalytics.unique_sharers).toFixed(1) 
                            : '0'}
                        </p>
                      </div>
                      <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                        <TrendingUp className="h-6 w-6 text-green-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Platform Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Share2 className="h-5 w-5 text-purple-600" />
                    Shares by Platform
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {Object.entries(shareAnalytics.by_platform || {}).map(([platform, count]) => {
                      const platformIcons = {
                        facebook: { icon: Facebook, color: 'text-blue-600', bg: 'bg-blue-50' },
                        instagram: { icon: Instagram, color: 'text-pink-600', bg: 'bg-gradient-to-br from-purple-50 to-pink-50' },
                        linkedin: { icon: Linkedin, color: 'text-blue-700', bg: 'bg-blue-50' },
                        whatsapp: { icon: MessageSquare, color: 'text-green-600', bg: 'bg-green-50' },
                        email: { icon: Mail, color: 'text-gray-600', bg: 'bg-gray-50' },
                        copy_link: { icon: Copy, color: 'text-purple-600', bg: 'bg-purple-50' },
                        download_image: { icon: Download, color: 'text-pink-600', bg: 'bg-pink-50' },
                        direct_link: { icon: Share2, color: 'text-amber-600', bg: 'bg-amber-50' },
                        twitter: { icon: Share2, color: 'text-sky-600', bg: 'bg-sky-50' }
                      };
                      const platformConfig = platformIcons[platform] || { icon: Share2, color: 'text-gray-600', bg: 'bg-gray-50' };
                      const IconComponent = platformConfig.icon;
                      
                      return (
                        <div key={platform} className={`p-4 rounded-lg ${platformConfig.bg} flex items-center gap-3`}>
                          <IconComponent className={`h-6 w-6 ${platformConfig.color}`} />
                          <div>
                            <p className="text-xs text-gray-500 capitalize">{platform.replace('_', ' ')}</p>
                            <p className="text-xl font-bold text-gray-900">{count}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {Object.keys(shareAnalytics.by_platform || {}).length === 0 && (
                    <p className="text-center text-gray-500 py-4">No shares recorded yet</p>
                  )}
                </CardContent>
              </Card>

              {/* Top Shared Posts */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-green-600" />
                    Top Shared Soul Food
                  </CardTitle>
                  <CardDescription>Most shared posts in the community</CardDescription>
                </CardHeader>
                <CardContent>
                  {shareAnalytics.top_shared_posts?.length > 0 ? (
                    <div className="space-y-3">
                      {shareAnalytics.top_shared_posts.map((post, index) => (
                        <div 
                          key={post.soul_food_id} 
                          className="flex items-center gap-4 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                        >
                          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center text-purple-700 font-bold">
                            {index + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 truncate">
                              {post.title || `Week of ${post.week_of}`}
                            </p>
                            <p className="text-sm text-gray-500 truncate">
                              "{post.quote_preview}"
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            {post.theme && (
                              <Badge variant="secondary" className="bg-purple-100 text-purple-700">
                                {post.theme}
                              </Badge>
                            )}
                            <Badge className="bg-green-100 text-green-700">
                              <Share2 className="h-3 w-3 mr-1" />
                              {post.share_count} shares
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-gray-500 py-4">No shares recorded yet</p>
                  )}
                </CardContent>
              </Card>

              {/* Refresh Button */}
              <div className="flex justify-end">
                <Button variant="outline" onClick={fetchShareAnalytics}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh Analytics
                </Button>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Edit Dialog */}
      <Dialog open={editMode} onOpenChange={setEditMode}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Edit {selectedItem?.type === 'soul_food' ? 'Soul Food' : 'Weekly Reflections'}
            </DialogTitle>
          </DialogHeader>
          
          {selectedItem?.type === 'soul_food' && (
            <div className="space-y-4">
              <div>
                <Label>Title</Label>
                <Input
                  value={editData.title || ''}
                  onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                />
              </div>
              <div>
                <Label>Quote/Scripture</Label>
                <Textarea
                  value={editData.quote_or_scripture || ''}
                  onChange={(e) => setEditData({ ...editData, quote_or_scripture: e.target.value })}
                  rows={3}
                />
              </div>
              <div>
                <Label>Source</Label>
                <Input
                  value={editData.source || ''}
                  onChange={(e) => setEditData({ ...editData, source: e.target.value })}
                  placeholder="e.g., Jeremiah 29:11 or Maya Angelou"
                />
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={editData.is_scripture}
                  onCheckedChange={(checked) => setEditData({ ...editData, is_scripture: checked })}
                />
                <Label>Is Scripture</Label>
              </div>
              <div>
                <Label>Reflection</Label>
                <Textarea
                  value={editData.reflection || ''}
                  onChange={(e) => setEditData({ ...editData, reflection: e.target.value })}
                  rows={4}
                />
              </div>
              <div>
                <Label>Theme</Label>
                <Input
                  value={editData.theme || ''}
                  onChange={(e) => setEditData({ ...editData, theme: e.target.value })}
                  placeholder="e.g., hope, boundaries, faith"
                />
              </div>
            </div>
          )}

          {selectedItem?.type === 'reflections' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-500">
                Edit the reflection prompts for week of {editData.week_of}
              </p>
              {editData.prompts?.map((prompt, idx) => (
                <div key={prompt.id} className="p-3 border rounded-lg space-y-2">
                  <div className="flex items-center gap-2">
                    <Badge>{idx + 1}</Badge>
                    <Input
                      value={prompt.theme || ''}
                      onChange={(e) => {
                        const newPrompts = [...editData.prompts];
                        newPrompts[idx].theme = e.target.value;
                        setEditData({ ...editData, prompts: newPrompts });
                      }}
                      placeholder="Theme"
                      className="w-32"
                    />
                  </div>
                  <Textarea
                    value={prompt.prompt || ''}
                    onChange={(e) => {
                      const newPrompts = [...editData.prompts];
                      newPrompts[idx].prompt = e.target.value;
                      setEditData({ ...editData, prompts: newPrompts });
                    }}
                    placeholder="Reflection prompt"
                    rows={2}
                  />
                  <Input
                    value={prompt.follow_up || ''}
                    onChange={(e) => {
                      const newPrompts = [...editData.prompts];
                      newPrompts[idx].follow_up = e.target.value;
                      setEditData({ ...editData, prompts: newPrompts });
                    }}
                    placeholder="Follow-up question (optional)"
                  />
                </div>
              ))}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditMode(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => handleUpdate(selectedItem.type, selectedItem.data.id, editData)}
              data-testid="save-edit-btn"
            >
              <Check className="h-4 w-4 mr-2" />
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Soul Food Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create Soul Food</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Title</Label>
              <Input
                value={newSoulFood.title}
                onChange={(e) => setNewSoulFood({ ...newSoulFood, title: e.target.value })}
                placeholder="e.g., Standing on Promise"
              />
            </div>
            <div>
              <Label>Quote/Scripture</Label>
              <Textarea
                value={newSoulFood.quote_or_scripture}
                onChange={(e) => setNewSoulFood({ ...newSoulFood, quote_or_scripture: e.target.value })}
                placeholder="Enter the quote or scripture..."
                rows={3}
              />
            </div>
            <div>
              <Label>Source</Label>
              <Input
                value={newSoulFood.source}
                onChange={(e) => setNewSoulFood({ ...newSoulFood, source: e.target.value })}
                placeholder="e.g., Jeremiah 29:11"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={newSoulFood.is_scripture}
                onCheckedChange={(checked) => setNewSoulFood({ ...newSoulFood, is_scripture: checked })}
              />
              <Label>Is Scripture</Label>
            </div>
            <div>
              <Label>Reflection</Label>
              <Textarea
                value={newSoulFood.reflection}
                onChange={(e) => setNewSoulFood({ ...newSoulFood, reflection: e.target.value })}
                placeholder="Your reflection message to the community..."
                rows={4}
              />
            </div>
            <div>
              <Label>Theme</Label>
              <Input
                value={newSoulFood.theme}
                onChange={(e) => setNewSoulFood({ ...newSoulFood, theme: e.target.value })}
                placeholder="e.g., hope, boundaries"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateSoulFood} data-testid="create-soul-food-submit">
              <Plus className="h-4 w-4 mr-2" />
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
